import { useState, useEffect } from "react";
import { paths, API_URL } from "../config/endpoints";

import { Availability } from "../types/properties/availability";
import { isTokenExpired, refreshToken } from "../services/authServices";

export const useGetUnitAvailability = (
  uid: string,
  fromDate?: string,
  toDate?: string
) => {
  const [unitAvailability, setUnitAvailability] = useState<Availability | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Compute default dates in UTC if not provided
  const todayUTC = new Date(
    Date.UTC(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate()
    )
  );
  const threeMonthsFromNowUTC = new Date(
    Date.UTC(
      todayUTC.getFullYear(),
      todayUTC.getMonth() + 3,
      todayUTC.getDate()
    )
  );

  const defaultFromDate = todayUTC.toISOString().split("T")[0]; // Format: YYYY-MM-DD
  const defaultToDate = threeMonthsFromNowUTC.toISOString().split("T")[0]; // Format: YYYY-MM-DD

  const effectiveFromDate = fromDate || defaultFromDate;
  const effectiveToDate = toDate || defaultToDate;

  const apiUrl = `${API_URL}/${paths.guest}/${paths.properties}/${uid}/${paths.availability}`;

  useEffect(() => {
    const fetchAvailability = async () => {
      setLoading(true);
      setError(null);

      try {
        let token = localStorage.getItem("token");

        // Check if the token is expired and refresh it if necessary
        if (!token || isTokenExpired(token)) {
          token = await refreshToken();
        }

        const response = await fetch(
          `${apiUrl}?fromDate=${effectiveFromDate}&toDate=${effectiveToDate}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch unit availability");
        }

        const data = await response.json();
        setUnitAvailability(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    if (uid) {
      fetchAvailability();
    }
  }, [uid, effectiveFromDate, effectiveToDate, apiUrl]);

  return { unitAvailability, loading, error };
};
